import { createFileRoute } from '@tanstack/react-router'
import { RegularLink } from '../../components/inline/RegularLink'

export const Route = createFileRoute('/faq/')({
  component: FAQPage,
})

function FAQPage() {
  const faqs = [
    {
      question: "Do attendees need to download an app?",
      answer: "No, participants can just enter the joining code on this website through their mobile browser. No downloads required! However, we also offer native mobile apps for iOS and Android for an enhanced experience."
    },
    {
      question: "Does it integrate with ProPresenter or PowerPoint?",
      answer: "Yes! Our desktop software captures your presentation output seamlessly and syncs it to the cloud in real-time. We support ProPresenter, PowerPoint, Keynote, and many other presentation tools."
    },
    {
      question: "How much does it cost?",
      answer: "We offer a free tier for small groups. For larger congregations and classrooms, we have affordable premium plans to support higher concurrency and additional features."
    },
    {
      question: "How do I get started?",
      answer: "Create a free account on our website, download the desktop encoder software, and configure it for your presentation tool. Then share the joining code with your audience!"
    },
    {
      question: "Can I use this for my church?",
      answer: "Absolutely! Sermon Slides is designed specifically for churches, classrooms, meetings, and any gathering where you want to share slides with participants on their personal devices."
    },
    {
      question: "What devices are supported?",
      answer: "Attendees can view slides on any device with a web browser (iPhone, Android, iPad, laptop, etc.). Our native apps are available for iOS and Android devices."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we take security seriously. All data is encrypted in transit and at rest. We comply with standard security practices and GDPR regulations."
    },
    {
      question: "Can I customize the slide presentation?",
      answer: "Yes! You can adjust the layout, add notes, customize fonts, and configure various display options through the encoder software."
    },
    {
      question: "What if something goes wrong?",
      answer: "We have comprehensive documentation, video tutorials, and email support available. For issues, you can reach out to our support team at support@sermon-slides.com"
    },
    {
      question: "Can I record or save the slides?",
      answer: "Yes, with our premium plans you can record presentations, save attendance data, and access historical slide sets."
    }
  ]

  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null)

  return (
    <div className="min-h-screen flex flex-col font-sans bg-stone-50 dark:bg-mist-950 text-stone-900 dark:text-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <RegularLink to="/" className="font-medium mb-8 inline-flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </RegularLink>

        <h1 className="text-4xl font-bold mb-4 text-stone-900 dark:text-white">Frequently Asked Questions</h1>
        <p className="text-lg text-stone-600 dark:text-stone-400 mb-12">Find answers to common questions about Sermon Slides</p>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details 
              key={index}
              className="group bg-white dark:bg-mist-900 border border-stone-200 dark:border-mist-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden"
              open={expandedIndex === index}
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
            >
              <summary className="flex cursor-pointer items-center justify-between gap-1.5 p-6 text-stone-900 dark:text-white font-medium hover:bg-purple-50 dark:bg-purple-950/40 transition-colors">
                <h3 className="text-lg text-left">{faq.question}</h3>
                <span className="shrink-0 rounded-full bg-purple-50 dark:bg-purple-950/40 p-1.5 text-purple-700 group-open:-rotate-180 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="size-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-6 pt-0 text-stone-600 dark:text-stone-400 leading-relaxed">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>

        <div className="mt-16 p-8 bg-gradient-to-br from-purple-50 to-orange-50 dark:from-purple-950/30 dark:to-orange-950/30 rounded-xl border border-purple-100 dark:border-purple-900/40">
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white mb-4">Still have questions?</h2>
          <p className="text-stone-600 dark:text-stone-400 mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@sermon-slides.com"
              className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Support
            </a>
            <a 
              href="https://learn.sermon-slides.com/docs"
              className="inline-flex items-center justify-center gap-2 border border-stone-300 dark:border-mist-700 hover:border-purple-600 hover:text-purple-600 text-stone-600 dark:text-stone-400 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C6.5 6.253 2 10.998 2 17s4.5 10.747 10 10.747c5.5 0 10-4.998 10-10.747S17.5 6.253 12 6.253z" />
              </svg>
              View Documentation
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

import React from 'react'
